import { Helmet } from "react-helmet-async";
import { useEffect, useState, useCallback } from "react";
import {
  Card,
  Table,
  Stack,
  Paper,
  Popover,
  TableRow,
  MenuItem,
  TableCell,
  Container,
  Typography,
  TableContainer,
  TableHead,
  CircularProgress,
  Box,
  Button,
  Menu,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
} from "@mui/material";

import Scrollbar from "../components/scrollbar";
import { useSearchParams, useNavigate } from "react-router-dom";
import { get, put, del, post } from "src/utils/networkUtils";
import { createHeadshotInContainer } from "src/utils/azureBlob";
import ScoreablesHeader from "src/components/scoreables/ScoreablesHeader";
import { getUserPermissions } from "src/utils/permissionsUtils";
import Iconify from "../components/iconify";
import { getFilenameForUpload } from "../components/PlayerHeadshot";
import { ScoreablePositions } from "src/utils/positionUtils";
import DeleteScoreableDialog from "src/components/scoreables/DeleteScoreableDialog";
import ScoreablesTableBody from "src/components/scoreables/ScoreablesTableBody";
import PositionGlossary from "src/components/scoreables/PositionGlossary";
import ConnectionMigrateDialog from "src/components/migrate/ConnectionMigrateDialog";
import useWithUrlTenant from "src/hooks/useTenant";
import useTenantCompetitions from "src/hooks/useTenantCompetitions";
import ScoreableStageScoringDialog from "src/components/scoreables/ScoreableStageScoringDialog";

const INJURY_STATUS_OPTIONS = [
  { value: 0, label: "None" },
  { value: 1, label: "Day-to-Day" },
  { value: 2, label: "Injured" },
  { value: 3, label: "Out" },
];

export default function ScoreablesPage() {
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(null);
  const [scoreables, setScoreables] = useState([]);
  const [teams, setTeams] = useState([]);
  const [team, setTeam] = useState([]);
  const [competition, setCompetition] = useState(null);
  const [seasons, setSeasons] = useState([]);
  const [selectedSeason, setSelectedSeason] = useState(null);
  const [isInternational, setIsInternational] = useState(false);
  const [ratings, setRatings] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [imageHash, setImageHash] = useState(new Date().toISOString());
  const [editScoreableNameIds, setEditScoreableNameIds] = useState([]);
  const [canTeamGenerate, setCanTeamGenerate] = useState(false);
  const [competitionScoreables, setCompetitionScoreables] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedScoreable, setSelectedScoreable] = useState(null);
  const [loadingScoreableId, setLoadingScoreableId] = useState(null);
  const [cardCount, setCardCount] = useState(-1);
  const [editPositionIds, setEditPositionIds] = useState([]);
  const [uploadingImageIds, setUploadingImageIds] = useState([]);
  const [connectionDialogeOpen, setConnectionDialogeOpen] = useState(false);
  const [scoreableStatsId, setScoreableStatsId] = useState(null);
  const [sport, setSport] = useState(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [teamsFile, setTeamsFile] = useState(null);
  const [playersFiles, setPlayersFiles] = useState([]);
  const [uploadError, setUploadError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [selectedPositions, setSelectedPositions] = useState([]);
  const [selectedInjuryStatuses, setSelectedInjuryStatuses] = useState([]);
  const [injuryFilterAnchor, setInjuryFilterAnchor] = useState(null);

  const { isViewer } = getUserPermissions();
  const withTenant = useWithUrlTenant();
  const { tenantCompetitions, tenantId } = useTenantCompetitions();

  const redirectToFirstCompetition = useCallback(() => {
    if (tenantCompetitions && tenantCompetitions.length > 0 && searchParams.has("competition_id")) {
      const newCompetitionId = tenantCompetitions[0]?.competitionId;
      if (newCompetitionId) {
        const currentSearchParams = new URLSearchParams(searchParams);
        currentSearchParams.set("competition_id", newCompetitionId);
        if (tenantId) {
          currentSearchParams.set("tenantId", tenantId);
        }
        navigate(`/dashboard/scoreables/competition-scoreables?${currentSearchParams.toString()}`, { replace: true });
      }
    }
  }, [tenantCompetitions, searchParams, tenantId, navigate]);

  // Fetch seasons on component mount
  useEffect(() => {
    get(withTenant('/admin/seasons')).then(function (response) {
      const seasonsData = response.data || [];
      setSeasons(seasonsData);
      // Set current season as default
      const currentSeason = seasonsData.find(season => season.isCurrentSeason);
      if (currentSeason) {
        setSelectedSeason(currentSeason.seasonId);
      } else if (seasonsData.length > 0) {
        setSelectedSeason(seasonsData[0].seasonId);
      }
    }).catch((error) => {
      console.error("Error fetching seasons:", error);
    });
  }, [withTenant]);

  // Ensure competition_id syncs with current tenant's first competition when tenantCompetitions update
  useEffect(() => {
    if (tenantCompetitions && tenantCompetitions.length > 0) {
      const currentCompetitionId = searchParams.get("competition_id");
      const hasCurrentInList = tenantCompetitions.some(
        (c) => String(c.competitionId) === String(currentCompetitionId)
      );
      // set sport from competition
      const competition = tenantCompetitions.find(
        (c) => +c.competitionId === +currentCompetitionId
      );
      setSport(competition?.sport);
      // If current competition is missing or not in this tenant's list, redirect to first
      if (!hasCurrentInList) {
        redirectToFirstCompetition();
      }
    }
  }, [tenantCompetitions, tenantId, searchParams, navigate, redirectToFirstCompetition]);

  // Listen for tenant changes and update competition_id to the first competition of the new tenant
  useEffect(() => {
    const handleTenantChange = (event) => {
      // Reset state when tenant changes
      setScoreables([]);
      setTeams([]);
      setTeam([]);
      setCompetitionScoreables([]);
      setSelectedPositions([]);
      setSelectedInjuryStatuses([]);
      
      // Only update if we have tenant competitions and we're on the competition page
      redirectToFirstCompetition();
    };

    // Add event listener for tenant changes
    window.addEventListener('tenantChanged', handleTenantChange);

    // Cleanup event listener on component unmount
    return () => {
      window.removeEventListener('tenantChanged', handleTenantChange);
    };
  }, [tenantCompetitions, searchParams, navigate, redirectToFirstCompetition]);

  useEffect(() => {
    if (searchParams.has("competition_id") && selectedSeason) {
      let competitionId = searchParams.get("competition_id");
      let isInternationalParam = searchParams.get("is_international") ?? false;
      setIsInternational(isInternationalParam);
      setCompetition(competitionId);
      let ratingsParam = searchParams.get("ratings") === "true";
      setRatings(ratingsParam);
      if (!ratingsParam) {
        get(`/admin/teams?competition_id=${competitionId}&seasonId=${selectedSeason}&isAdmin=true`).then(function (
          response
        ) {
          const teams = response.teams;
          setTeams(teams);
          setTeam(teams[0].scoreableId);
          get(
            `/admin/teams/${teams[0].scoreableId}/scoreables?isInternational=${isInternationalParam}`
          ).then(function (response) {
            const scoreablesRows = response.scoreables;
            scoreablesRows.unshift(response.team);
            setScoreables(scoreablesRows);
          });
          get(`/admin/competitions/${competitionId}/scoreables?seasonId=${selectedSeason}`).then(function (
            response
          ) {
            setCompetitionScoreables(response.scoreables);
            const competitionScoreable = response.scoreables.find(
              (item) => item.scoreable_id === teams[0].scoreableId
            );
            if (competitionScoreable) {
              setCanTeamGenerate(competitionScoreable.can_generate);
            }
          });
        });
      }
    }
  }, [searchParams, selectedSeason]);

  const handleSaveRatings = () => {
    const updateScoreables = scoreables.map((scoreable) => {
      return {
        scoreableId: scoreable.scoreableId,
        visibility: scoreable.visibility,
        rating: scoreable.rating,
        isActive: scoreable.isActive,
        isInjured: scoreable.isInjured,
        canGenerate: scoreable.canGenerate,
        firstname: scoreable.firstname,
        lastname: scoreable.lastname,
        shortname: scoreable.shortname,
        position: scoreable.position,
        injuryStatus: scoreable.injuryStatus,
      };
    });

    setLoading(true);

    put(`/admin/scoreables`, {
      updateScoreables,
      competitionId: competition,
      tenantId: tenantId,
    }).then(function (response) {
      window.location.reload();
    });
  };
  const handleSeasonChange = (event) => {
    setSelectedSeason(event.target.value);
    setScoreables(null);
    setTeams([]);
    setTeam([]);
    setSelectedPositions([]);
    setSelectedInjuryStatuses([]);
  };

  const handleTogglePosition = (value) => {
    setSelectedPositions((prev) =>
      prev.includes(value)
        ? prev.filter((v) => v !== value)
        : [...prev, value]
    );
  };

  const handleClearPositions = () => {
    setSelectedPositions([]);
  };

  const handleToggleInjuryStatus = (value) => {
    setSelectedInjuryStatuses((prev) =>
      prev.includes(value)
        ? prev.filter((v) => v !== value)
        : [...prev, value]
    );
  };

  const handleChangeSelect = (event) => {
    setScoreables(null);
    setSelectedPositions([]);
    setSelectedInjuryStatuses([]);
    const teamId = event.target.value;
    setTeam(teamId);
    if (!ratings) {
      get(
        `/admin/teams/${teamId}/scoreables?isInternational=${isInternational}`
      ).then(function (response) {
        const scoreablesRows = response.scoreables;
        scoreablesRows.unshift(response.team);
        setScoreables(scoreablesRows);
      });

      const competitionScoreable = competitionScoreables.find(
        (item) => item.scoreable_id === teamId
      );

      if (competitionScoreable) {
        setCanTeamGenerate(competitionScoreable.can_generate);
      } else {
        setCanTeamGenerate(false);
      }
    } else {
      get(withTenant(`/admin/ratings/${teamId}/scoreables?seasonId=${selectedSeason}`)).then(function (response) {
        const scoreablesRows = response.scoreables;
        setScoreables(scoreablesRows);
      });
    }
  };

  const handlePositionChange = (event, scoreableId) => {
    const index = scoreables.findIndex(
      (scoreable) => scoreable.scoreableId === scoreableId
    );
    if (index !== -1) {
      scoreables[index].position = parseInt(event.target.value);
      setScoreables([...scoreables]);
    }
  };

  const showEditPosition = (scoreableId) => {
    setEditPositionIds([...editPositionIds, scoreableId]);
  };

  const handleCloseMenu = () => {
    setOpen(null);
  };
  const handleChangeRating = (event, scoreableId) => {
    const index = scoreables.findIndex(
      (scoreable) => scoreable.scoreableId === scoreableId
    );
    if (index !== -1) {
      scoreables[index].rating = parseInt(event.target.value);
      setScoreables([...scoreables]);
    }
  };

  const handleCheckboxChange = (e, scoreableId, flagName) => {
    const index = scoreables.findIndex(
      (scoreable) => scoreable.scoreableId === scoreableId
    );
    if (index !== -1) {
      scoreables[index][flagName] = e.target.checked;
      setScoreables([...scoreables]);
    }
  };

  const handleInjuryChange = (event, scoreableId) => {
    const index = scoreables.findIndex(
      (scoreable) => scoreable.scoreableId === scoreableId
    );
    if (index !== -1) {
      scoreables[index].injuryStatus = parseInt(event.target.value);
      setScoreables([...scoreables]);
    }
  };

  const handleVisibilityChange = (e, scoreableId) => {
    const index = scoreables.findIndex(
      (scoreable) => scoreable.scoreableId === scoreableId
    );
    if (index !== -1) {
      scoreables[index].visibility = e.target.checked;
      setScoreables([...scoreables]);
    }
  };
  
  const handleUpdateTeamGenerate = (e) => {
    setCanTeamGenerate(e.target.checked);
    put(`/admin/team/can-generate`, {
      competitionId: competition,
      scoreableId: team,
      canGenerate: e.target.checked,
      tenantId: tenantId,
    }).then(function (response) {
      console.log("update flag success");
      get(`/admin/competitions/${competition}/scoreables`).then(function (
        response
      ) {
        setCompetitionScoreables(response.scoreables);
        const competitionScoreable = response.scoreables.find(
          (item) => item.scoreable_id === team
        );
        if (competitionScoreable) {
          setCanTeamGenerate(competitionScoreable.can_generate);
        }
      });
    });
  };
  const handleChangeName = (e, scoreableId) => {
    const { id, value } = e.target;
    const index = scoreables.findIndex(
      (scoreable) => scoreable.scoreableId === scoreableId
    );
    if (index !== -1) {
      scoreables[index][id] = value;
      setScoreables([...scoreables]);
    }
  };
  const showEditName = (scoreableId) => {
    editScoreableNameIds.push(scoreableId);
    setEditScoreableNameIds([...editScoreableNameIds]);
  };

  const handleDeleteClick = (scoreable) => {
    setLoadingScoreableId(scoreable.scoreableId);

    get(`/admin/scoreable-marketplace/${scoreable.scoreableId}`)
      .then(function (response) {
        setCardCount(response.cardCount || 0);
        setSelectedScoreable(scoreable);
        setDeleteDialogOpen(true);
        setLoadingScoreableId(null);
      })
      .catch(function (error) {
        console.error("Error fetching card count:", error);
        setCardCount(0);
        setSelectedScoreable(scoreable);
        setDeleteDialogOpen(true);
        setLoadingScoreableId(null);
      });
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setSelectedScoreable(null);
  };

  const handleConfirmDelete = () => {
    if (!selectedScoreable) return;

    setDeleteDialogOpen(false);
    setLoadingScoreableId(selectedScoreable.scoreableId);

    del(`/admin/scoreable-marketplace/${selectedScoreable.scoreableId}`)
      .then(function (response) {
        setLoadingScoreableId(null);
        setSelectedScoreable(null);
      })
      .catch(function (error) {
        console.error("Error deleting scoreable from marketplace:", error);
        setLoadingScoreableId(null);
        setSelectedScoreable(null);
      });
  };

  const uploadPlayerImage = async (event, row) => {
    if (event.target.files.length > 0) {
      const file = event.target.files[0];
      const filename = getFilenameForUpload(row);
      const renamedFile = new File([file], filename, { type: file.type });
      setUploadingImageIds([...uploadingImageIds, row.scoreableId]);

      try {
        await createHeadshotInContainer(renamedFile, filename);
        setImageHash(new Date().toISOString());
        setUploadingImageIds(
          uploadingImageIds.filter((id) => id !== row.scoreableId)
        );
      } catch (error) {
        console.error("Error uploading image:", error);
        setUploadingImageIds(
          uploadingImageIds.filter((id) => id !== row.scoreableId)
        );
      }
    }
  };

  const handleDataMigration = () => {
    setConnectionDialogeOpen(true)
  };

  const handleImportScoreablesFromCSV = () => {
    setImportDialogOpen(true);
    setUploadError(null);
  };

  const handleExportScoreablesToCSV = () => {
    const columns = [
      { key: 'scoreableId',     label: 'Scoreable ID'  },
      { key: 'teamExternalRef', label: 'Feed ID'        },
      { key: 'firstname',       label: 'First Name'     },
      { key: 'lastname',        label: 'Last Name'      },
      { key: 'shortname',       label: 'Short Name'     },
      { key: 'team',            label: 'Team'           },
      { key: 'position',        label: 'Position'       },
      { key: 'rating',          label: 'Rating'         },
      { key: 'isActive',        label: 'Active'         },
      { key: 'isInjured',       label: 'Injured'        },
      { key: 'injuryStatus',    label: 'Injury Status'  },
      { key: 'canGenerate',     label: 'Can Generate'   },
    ];

    const positionMap = Object.fromEntries(
      Object.entries(ScoreablePositions).map(([name, value]) => [value, name])
    );

    const fieldMaps = {
      position:     positionMap,
      injuryStatus: { 0: 'None', 1: 'Day-to-Day', 2: 'Injured', 3: 'Out' },
      isActive:     { true: 'Yes', false: 'No' },
      isInjured:    { true: 'Yes', false: 'No' },
      canGenerate:  { true: 'Yes', false: 'No' },
      rating:       { 0: 'Base', 1: 'Silver', 2: 'Gold', 3: 'All-Star' },
    };

    const csvHeaders = columns.map(({ label }) => `"${label}"`).join(',');

    const buildAndDownloadCsv = (data) => {
      const rows = data.map((s) =>
        columns.map(({ key }) => {
          const raw = s[key] ?? '';
          const val = fieldMaps[key]?.[raw] ?? fieldMaps[key]?.[String(raw)] ?? raw;
          return `"${String(val).replace(/"/g, '""')}"`;
        }).join(',')
      );
      const csv = [csvHeaders, ...rows].join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'scoreables-'+new Date().toISOString()+'.csv';
      a.click();
      URL.revokeObjectURL(url);
    };

    setExporting(true);
    get(`/admin/scoreables?tenantId=${tenantId}`)
      .then((response) => buildAndDownloadCsv(response.scoreables || []))
      .catch((error) => console.error('Error exporting scoreables:', error))
      .finally(() => setExporting(false));
  };

  const handleCloseImportDialog = () => {
    setImportDialogOpen(false);
    setTeamsFile(null);
    setPlayersFiles([]);
    setUploadError(null);
  };

  const handleTeamsFileChange = (event) => {
    const file = event.target.files[0];
    if (file && file.name.toLowerCase().endsWith('.csv')) {
      setTeamsFile(file);
      setUploadError(null);
    } else {
      setUploadError('Please select a valid CSV file for teams');
    }
  };

  const handlePlayersFilesChange = (event) => {
    const files = Array.from(event.target.files);
    const csvFiles = files.filter(file => file.name.toLowerCase().endsWith('.csv'));
    
    if (csvFiles.length !== files.length) {
      setUploadError('All player files must be CSV files');
      return;
    }
    
    setPlayersFiles([...playersFiles, ...csvFiles]);
    setUploadError(null);
  };

  const handleRemoveTeamsFile = () => {
    setTeamsFile(null);
  };

  const handleRemovePlayersFile = (index) => {
    const updatedFiles = playersFiles.filter((_, i) => i !== index);
    setPlayersFiles(updatedFiles);
  };

  const handleSubmitImport = async () => {
    if (!teamsFile) {
      setUploadError('Teams CSV file is required');
      return;
    }

    setUploading(true);
    setUploadError(null);

    try {
      const formData = new FormData();
      formData.append('teamsFile', teamsFile);
      formData.append('tenantId', tenantId || 1);
      formData.append('competitionId', competition || 1);

      playersFiles.forEach((file) => {
        formData.append('playersFiles', file);
      });

      await post(
        `/admin/scoreables/upload`, 
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      handleCloseImportDialog();
      window.location.reload();
    } catch (error) {
      console.error('Error importing scoreables:', error);
      setUploadError(error?.response?.data?.message || error.message || 'Failed to import scoreables. Please try again.');
      setUploading(false);
    }
  };

  const filteredScoreables =
    scoreables && (selectedPositions.length > 0 || selectedInjuryStatuses.length > 0)
      ? scoreables.filter((row) => {
          const positionMatch =
            selectedPositions.length === 0 ||
            selectedPositions.includes(row.position);
          const injuryMatch =
            selectedInjuryStatuses.length === 0 ||
            selectedInjuryStatuses.includes(row.injuryStatus || 0);
          return positionMatch && injuryMatch;
        })
      : scoreables;

  return (
    <>
      <Helmet>
        <title> Scoreables </title>
      </Helmet>

      <Container>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={2}
        >
          <Typography variant="h4" gutterBottom>
            Scoreables
          </Typography>
          <Button
            variant="contained"
            startIcon={<Iconify icon="eva:swap-outline" />}
            onClick={handleDataMigration}
          >
            Migration
          </Button>
        </Stack>

        <PositionGlossary
          sport={sport}
          selectedPositions={selectedPositions}
          onTogglePosition={handleTogglePosition}
          onClearPositions={handleClearPositions}
        />

        <Stack
          direction="row"
          alignItems="center"
          style={{ justifyContent: 'flex-end' }}
          gap={1}
          mb={2}
        >
          <Button
            variant="outlined"
            startIcon={exporting ? <CircularProgress size={16} /> : <Iconify icon="eva:download-outline" />}
            onClick={handleExportScoreablesToCSV}
            disabled={!scoreables || scoreables.length === 0 || exporting}
          >
            {exporting ? 'Exporting...' : 'Export Scoreables to CSV'}
          </Button>
          <Button
            variant="contained"
            startIcon={<Iconify icon="eva:upload-outline" />}
            onClick={handleImportScoreablesFromCSV}
          >
            Import Scoreables from CSV
          </Button>
        </Stack>

        <ScoreablesHeader
          ratings={ratings}
          canTeamGenerate={canTeamGenerate}
          handleUpdateTeamGenerate={handleUpdateTeamGenerate}
          team={team}
          handleChangeSelect={handleChangeSelect}
          teams={teams}
          handleSaveRatings={handleSaveRatings}
          seasons={seasons}
          selectedSeason={selectedSeason}
          handleSeasonChange={handleSeasonChange}
        />
        <Card>
          {scoreables === null || loading ? (
            <Box
              sx={{ display: "flex", justifyContent: "center" }}
              style={{ padding: 20 }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <Scrollbar>
              <TableContainer component={Paper}>
                <Table sx={{ minWidth: 450 }} aria-label="simple table">
                  <TableHead>
                    <TableRow>
                      <TableCell>#</TableCell>
                      <TableCell>Feed ID</TableCell>
                      <TableCell>Visibility</TableCell>
                      <TableCell>Rating</TableCell>
                      <TableCell>Active/Generate</TableCell>
                      <TableCell>
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={0.5}
                        >
                          <span>Injury</span>
                          <IconButton
                            size="small"
                            onClick={(e) =>
                              setInjuryFilterAnchor(e.currentTarget)
                            }
                            color={
                              selectedInjuryStatuses.length > 0
                                ? "primary"
                                : "default"
                            }
                            title="Filter by injury status"
                          >
                            <Iconify icon="eva:funnel-outline" width={18} />
                          </IconButton>
                        </Stack>
                        <Menu
                          anchorEl={injuryFilterAnchor}
                          open={Boolean(injuryFilterAnchor)}
                          onClose={() => setInjuryFilterAnchor(null)}
                        >
                          {INJURY_STATUS_OPTIONS.map((option) => (
                            <MenuItem
                              key={option.value}
                              onClick={() =>
                                handleToggleInjuryStatus(option.value)
                              }
                            >
                              <Checkbox
                                size="small"
                                checked={selectedInjuryStatuses.includes(
                                  option.value
                                )}
                              />
                              {option.label}
                            </MenuItem>
                          ))}
                          {selectedInjuryStatuses.length > 0 && (
                            <MenuItem
                              onClick={() => setSelectedInjuryStatuses([])}
                              sx={{ color: "error.main", fontWeight: 600 }}
                            >
                              Clear filter
                            </MenuItem>
                          )}
                        </Menu>
                      </TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Position</TableCell>
                      <TableCell>Headshots</TableCell>
                      {ratings && <TableCell>Card Count</TableCell>}
                      <TableCell>Marketplace</TableCell>
                    </TableRow>
                  </TableHead>
                  <ScoreablesTableBody
                    scoreables={filteredScoreables}
                    handleVisibilityChange={handleVisibilityChange}
                    ratings={ratings}
                    imageHash={imageHash}
                    editScoreableNameIds={editScoreableNameIds}
                    editPositionIds={editPositionIds}
                    uploadingImageIds={uploadingImageIds}
                    loadingScoreableId={loadingScoreableId}
                    handleChangeRating={handleChangeRating}
                    handleCheckboxChange={handleCheckboxChange}
                    showEditName={showEditName}
                    handleChangeName={handleChangeName}
                    handlePositionChange={handlePositionChange}
                    showEditPosition={showEditPosition}
                    uploadPlayerImage={uploadPlayerImage}
                    handleDeleteClick={handleDeleteClick}
                    setEditPositionIds={setEditPositionIds}
                    handleInjuryChange={handleInjuryChange}
                    onStageScoringStatsClick={setScoreableStatsId}
                    sport={sport}
                  />
                </Table>
              </TableContainer>
            </Scrollbar>
          )}
        </Card>
      </Container>
      {!isViewer && (
        <Popover
          open={Boolean(open)}
          anchorEl={open}
          onClose={handleCloseMenu}
          anchorOrigin={{ vertical: "top", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          PaperProps={{
            sx: {
              p: 1,
              width: 140,
              "& .MuiMenuItem-root": {
                px: 1,
                typography: "body2",
                borderRadius: 0.75,
              },
            },
          }}
        >
          <MenuItem sx={{ color: "error.main" }}>
            <Iconify icon={"eva:trash-2-outline"} sx={{ mr: 2 }} />
            Delete
          </MenuItem>
        </Popover>
      )}

      <DeleteScoreableDialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleConfirmDelete}
        selectedScoreable={selectedScoreable}
        cardCount={cardCount}
      />

      <ConnectionMigrateDialog
        open={connectionDialogeOpen}
        onClose={() => setConnectionDialogeOpen(false)}
        title="Scoreables Data Migration"
        description="Migrates Scoreables (First & Last Name, Active, DTD, Generate) of selected season from current database to below database. It does not insert new scoreables, only updates existing scoreables."
        apiEndpoint={withTenant("/admin/scoreables/migration")}
        entityName="rows of Scoreables"
        additionalParams={{ seasonId: selectedSeason }}
      />
      {scoreableStatsId && (
        <ScoreableStageScoringDialog
          onClose={() => setScoreableStatsId(null)}
          scoreableId={scoreableStatsId}
          name={scoreables && scoreables.find(s => s.scoreableId === scoreableStatsId)?.displayName}
          sport={sport}
        />
      )}

      <Dialog 
        open={importDialogOpen} 
        onClose={handleCloseImportDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Import Scoreables from CSV
          <IconButton
            aria-label="close"
            onClick={handleCloseImportDialog}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Iconify icon="eva:close-outline" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {uploadError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {uploadError}
            </Alert>
          )}

          <Stack spacing={3}>
            {/* Teams File Upload */}
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                Teams CSV File <span style={{ color: 'red' }}>*</span>
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Required columns: "Full Team Name"
              </Typography>
              
              {!teamsFile ? (
                <Button
                  variant="outlined"
                  component="label"
                  fullWidth
                  startIcon={<Iconify icon="eva:cloud-upload-outline" />}
                >
                  Select Teams CSV
                  <input
                    type="file"
                    hidden
                    accept=".csv"
                    onChange={handleTeamsFileChange}
                  />
                </Button>
              ) : (
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Iconify icon="eva:file-text-outline" />
                      <Typography variant="body2">{teamsFile.name}</Typography>
                    </Stack>
                    <IconButton 
                      size="small" 
                      onClick={handleRemoveTeamsFile}
                      color="error"
                    >
                      <Iconify icon="eva:trash-2-outline" />
                    </IconButton>
                  </Stack>
                </Paper>
              )}
            </Box>

            {/* Players Files Upload */}
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                Players CSV Files <span style={{ color: 'red' }}>*</span>
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Required columns: "First & Last Name", "Position", "Jersey Number"
              </Typography>
              
              <Button
                variant="outlined"
                component="label"
                fullWidth
                startIcon={<Iconify icon="eva:cloud-upload-outline" />}
                sx={{ mb: playersFiles.length > 0 ? 2 : 0 }}
              >
                Add Players CSV Files
                <input
                  type="file"
                  hidden
                  accept=".csv"
                  multiple
                  onChange={handlePlayersFilesChange}
                />
              </Button>

              {playersFiles.length > 0 && (
                <Paper variant="outlined">
                  <List dense>
                    {playersFiles.map((file, index) => (
                      <ListItem key={index}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Iconify icon="eva:file-text-outline" />
                          <ListItemText 
                            primary={file.name}
                            secondary={`Team ${index + 1}`}
                          />
                        </Stack>
                        <ListItemSecondaryAction>
                          <IconButton 
                            edge="end" 
                            size="small"
                            onClick={() => handleRemovePlayersFile(index)}
                            color="error"
                          >
                            <Iconify icon="eva:trash-2-outline" />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              )}
            </Box>

            <Alert severity="info">
              <Typography variant="body2">
                <strong>Note:</strong>
                <br />
                <br />• Teams CSV must include the{" "}
                <strong>"Full Team Name"</strong> column.
                <br />• Players CSV files must include{" "}
                <strong>"First & Last Name"</strong>,{" "}
                <strong>"Position"</strong>, and{" "}
                <strong>"Jersey Number"</strong> columns.
                <br />
                • Each player CSV file will be associated with teams in order
                (Team 1, Team 2, etc.).
                <br />
                <br />
                <strong>How Player Records Are Processed:</strong>
                <br />• The system assigns an <strong>ExternalId</strong> to
                each player based on their row number in the uploaded CSV file
                (Row 1 = 1, Row 2 = 2, etc.).
                <br />• If a player with the same row number (ExternalId)
                already exists for the same tenant, the record will be{" "}
                <strong>updated</strong>.
                <br />• If no matching ExternalId is found, a new player record
                will be <strong>inserted</strong>.
                <br />
                <br />
                <strong>Columns Updated (if player already exists):</strong>
                <br />
                • First Name
                <br />
                • Last Name
                <br />
                • Jersey Number
                <br />
                • Position
              </Typography>
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseImportDialog} disabled={uploading}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmitImport}
            variant="contained"
            disabled={!teamsFile || uploading}
            startIcon={uploading ? <CircularProgress size={20} /> : <Iconify icon="eva:upload-outline" />}
          >
            {uploading ? 'Importing...' : 'Import'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
